import React, { useState } from 'react'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import TemplateBuilder from './TemplateBuilder'
import TemplateViewer from './components/TemplateViewer'
import NotificationSystem from './components/common/NotificationSystem'
import { useAccessibility } from './hooks/useAccessibility'
import { detectSubdomain } from './utils/subdomainDetector'

// Chatwoot URL for redirects when not authenticated
const CHATWOOT_URL = import.meta.env.VITE_CHATWOOT_URL || 'https://crm.godreamx.com'

// Check if running in local development mode
const IS_LOCAL_DEV = typeof window !== 'undefined' && (
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1'
)

function AppContent() {
  const authContext = useAuth()
  const { isAuthenticated, loading, redirectToLogin } = authContext

  // Initialize accessibility features
  useAccessibility()

  // Detect if we're on a user subdomain (*.users.godreamx.com)
  // This is for PREVIEW/DEVELOPMENT mode, NOT production serving
  // Production serving is handled by users-app
  const subdomainInfo = React.useMemo(() => {
    try {
      return detectSubdomain()
    } catch (error) {
      console.error('Error detecting subdomain:', error)
      return {
        isUserSubdomain: false,
        orgName: null,
        mode: 'editor'
      }
    }
  }, [])
  const { isUserSubdomain, orgName, mode } = subdomainInfo

  // Expose auth context to window for storage manager access
  React.useEffect(() => {
    window.authContext = authContext
    return () => {
      delete window.authContext
    }
  }, [authContext])

  // If we're in viewer mode (user subdomain or ?preview=orgName), show the template viewer
  // This is for DEVELOPMENT/TESTING only - production uses users-app
  if (isUserSubdomain && orgName) {
    return <TemplateViewer orgName={orgName} />
  }

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100" role="status" aria-live="polite">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-lg text-gray-600">Loading...</div>
        </div>
        <span className="sr-only">Application is loading</span>
      </div>
    )
  }

  // If authenticated, show the Template Builder
  if (isAuthenticated) {
    return (
      <>
        <a href="#main-content" className="skip-to-main">Skip to main content</a>
        <TemplateBuilder />
        <NotificationSystem />
      </>
    )
  }

  // Not authenticated - show redirect message and redirect to Chatwoot
  // For local dev, show a token input form instead
  if (IS_LOCAL_DEV) {
    return <LocalDevLogin />
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
        <div className="text-6xl mb-4">🔐</div>
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Authentication Required</h1>
        <p className="text-gray-600 mb-6">
          Please login through GoDreamX CRM to access the Template Builder.
        </p>
        <button
          onClick={redirectToLogin}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
        >
          Go to CRM Login
        </button>
        <p className="text-sm text-gray-500 mt-4">
          You will be redirected to the CRM where you can login and access the Template Builder.
        </p>
      </div>
      <NotificationSystem />
    </div>
  )
}

// Local development login component - uses inline styles to avoid CSS issues
function LocalDevLogin() {
  const [token, setToken] = useState('')
  const [error, setError] = useState('')

  const handleLogin = () => {
    if (!token.trim()) {
      setError('Please enter a JWT token')
      return
    }
    try {
      localStorage.setItem('jwt', token.trim())
      localStorage.setItem('token', token.trim())
      window.location.reload()
    } catch (e) {
      setError('Failed to save token: ' + e.message)
    }
  }

  const containerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#1a1a2e',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  }

  const cardStyle = {
    backgroundColor: '#16213e',
    padding: '32px',
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
    maxWidth: '500px',
    width: '90%',
    textAlign: 'center'
  }

  const titleStyle = {
    color: '#ffffff',
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '8px'
  }

  const subtitleStyle = {
    color: '#a0a0a0',
    marginBottom: '20px'
  }

  const codeStyle = {
    display: 'block',
    backgroundColor: '#0f3460',
    color: '#00ff88',
    padding: '12px',
    borderRadius: '8px',
    fontSize: '13px',
    textAlign: 'left',
    marginBottom: '20px',
    fontFamily: 'monospace'
  }

  const textareaStyle = {
    width: '100%',
    height: '100px',
    padding: '12px',
    backgroundColor: '#0f3460',
    color: '#ffffff',
    border: '2px solid #1a1a2e',
    borderRadius: '8px',
    fontSize: '12px',
    fontFamily: 'monospace',
    resize: 'vertical',
    marginBottom: '16px',
    boxSizing: 'border-box'
  }

  const buttonStyle = {
    width: '100%',
    padding: '14px 24px',
    backgroundColor: '#e94560',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer'
  }

  const errorStyle = {
    color: '#ff6b6b',
    marginBottom: '16px'
  }

  const footerStyle = {
    color: '#666',
    fontSize: '12px',
    marginTop: '16px'
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔧</div>
        <h1 style={titleStyle}>Local Development Mode</h1>
        <p style={subtitleStyle}>
          Paste your JWT token below to login. Generate one with:
        </p>
        <code style={codeStyle}>
          cd templatebuilderbackend<br/>
          node scripts/dev-login.js email@example.com
        </code>

        <textarea
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="Paste JWT token here..."
          style={textareaStyle}
        />

        {error && (
          <p style={errorStyle}>{error}</p>
        )}

        <button
          onClick={handleLogin}
          style={buttonStyle}
          onMouseOver={(e) => e.target.style.backgroundColor = '#ff6b6b'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#e94560'}
        >
          Login with Token
        </button>

        <p style={footerStyle}>
          This login form only appears on localhost
        </p>
      </div>
    </div>
  )
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}

export default App
